/**
 * The dashboard's only outbound channel.
 *
 * There is exactly one place in the browser bundle that performs a network call,
 * and it targets our own worker. No provider SDK, no provider hostname and no
 * credential appears in this app.
 */

const API_BASE = import.meta.env.VITE_API_URL ?? '';

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly correlationId: string | null;

  constructor(status: number, code: string, message: string, correlationId: string | null) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.correlationId = correlationId;
  }
}

export interface ApiRequestOptions {
  readonly method?: 'GET' | 'POST' | 'PATCH' | 'DELETE';
  readonly body?: unknown;
  readonly signal?: AbortSignal;
  /** Supabase access token. The worker verifies it; we never decode it here. */
  readonly accessToken?: string | null;
}

export async function apiRequest<T>(path: string, options: ApiRequestOptions = {}): Promise<T> {
  const { method = 'GET', body, signal, accessToken } = options;

  const response = await fetch(`${API_BASE}${path}`, {
    method,
    credentials: 'include',
    headers: {
      accept: 'application/json',
      ...(accessToken ? { authorization: `Bearer ${accessToken}` } : {}),
      ...(body === undefined ? {} : { 'content-type': 'application/json' }),
    },
    ...(body === undefined ? {} : { body: JSON.stringify(body) }),
    ...(signal ? { signal } : {}),
  });

  const correlationId = response.headers.get('x-correlation-id');

  if (!response.ok) {
    let code = 'request_failed';
    let message = `Request failed with status ${response.status}`;
    try {
      const payload = (await response.json()) as { error?: string; message?: string };
      code = payload.error ?? code;
      message = payload.message ?? code;
    } catch {
      // A non-JSON error body is itself the message we can safely show.
    }
    throw new ApiError(response.status, code, message, correlationId);
  }

  if (response.status === 204) return undefined as T;
  return (await response.json()) as T;
}
